import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import ErrorBoundary from "./components/ErrorBoundary";
import { SEOProvider } from "@/components/SEO/SEOProvider";
import { SecurityProvider } from "@/components/SecurityProvider";
import { HelmetProvider } from 'react-helmet-async';
import ScrollToTop from "./components/ScrollToTop";
import Header from "./components/Header";
import Footer from "./components/Footer";
import StickyButton from "./components/StickyButton";
import { CookieConsent, CookieDebugTools } from "./components/CookieConsent";
import { PWAPrompt, PWAStatus } from "./components/PWAPrompt";
import { lazy, Suspense, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useMaintenanceMode } from "@/hooks/useMaintenanceMode";

// Eager load only the landing page for optimal initial load
import Home from "./pages/Home";
import MaintenanceMode from "./pages/MaintenanceMode";

// Lazy load all other routes to reduce initial bundle size
const Guides = lazy(() => import("./pages/Guides"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Contact = lazy(() => import("./pages/Contact"));
const Auth = lazy(() => import("./pages/Auth"));
const NotFound = lazy(() => import("./pages/NotFound"));
const MentionsLegales = lazy(() => import("./pages/MentionsLegales"));
const PolitiqueConfidentialite = lazy(() => import("./pages/PolitiqueConfidentialite"));
const APropos = lazy(() => import("./pages/APropos"));
const Admin = lazy(() => import("./pages/Admin"));
const GuideEntry = lazy(() => import("./pages/GuideEntry"));
const CMSContent = lazy(() => import("@/components/CMSContent").then(m => ({ default: m.CMSContent })));

const queryClient = new QueryClient();

const MaintenanceRedirect = ({ children }: { children: React.ReactNode }) => {
  const { data: isMaintenanceMode, isLoading } = useMaintenanceMode();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (
      !isLoading &&
      isMaintenanceMode &&
      location.pathname !== "/maintenance" &&
      !location.pathname.startsWith("/admin") &&
      !location.pathname.startsWith("/auth")
    ) {
      navigate("/maintenance", { replace: true });
    }
  }, [isMaintenanceMode, isLoading, navigate, location]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Chargement...</div>
      </div>
    );
  }

  return <>{children}</>;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ErrorBoundary>
    <HelmetProvider>
      <SEOProvider>
        <SecurityProvider>
          <TooltipProvider>
            <AuthProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <ScrollToTop />
              <Routes>
                <Route path="/maintenance" element={<MaintenanceMode />} />
                <Route path="*" element={
                  <MaintenanceRedirect>
                    <div className="min-h-screen flex flex-col bg-background">
                      <Header />
                      <main className="flex-1">
                        <Suspense fallback={<div className="min-h-[60vh] flex items-center justify-center"><div className="animate-pulse text-muted-foreground">Chargement...</div></div>}>
                          <Routes>
                            <Route path="/" element={<Home />} />
                            <Route path="/guides" element={<Guides />} />
                            <Route path="/guides/:slug" element={<GuideEntry />} />
                            <Route path="/faq" element={<FAQ />} />
                            <Route path="/faq/:slug" element={<CMSContent contentType="faq" />} />
                            <Route path="/contact" element={<Contact />} />
                            <Route path="/projet" element={<Contact />} />
                            <Route path="/auth" element={<Auth />} />
                            <Route path="/mentions-legales" element={<MentionsLegales />} />
                            <Route path="/politique-confidentialite" element={<PolitiqueConfidentialite />} />
                            <Route path="/a-propos" element={<APropos />} />
                            <Route path="/admin" element={<Admin />} />
                            <Route path="*" element={<NotFound />} />
                          </Routes>
                        </Suspense>
                      </main>
                      <Footer />
                      <StickyButton />
                      <CookieConsent />
                      <CookieDebugTools />
                    </div>
                  </MaintenanceRedirect>
                } />
              </Routes>
            </BrowserRouter>
          </AuthProvider>
        </TooltipProvider>
        </SecurityProvider>
      </SEOProvider>
    </HelmetProvider>
    </ErrorBoundary>
  </QueryClientProvider>
);

export default App;
